/**
 * English locale — the source of truth for i18n keys.
 *
 * Every UI string lives here as a flat key → string map. `LocaleKey` is
 * derived from this object, so a typo'd key in a `t()` call is a type error,
 * and the Hindi dictionary is type-checked against the same key set
 * (see locales/hi.ts). Keys are namespaced `surface.element`.
 *
 * Interpolation uses `{var}` placeholders: t('common.greeting', { name }).
 */
export const en = {
  // ── Common / switcher ────────────────────────────────────────────────
  'common.language': 'Language',
  'common.languageSwitchTo': 'Switch language to {language}',
  'common.cancel': 'Cancel',
  'common.practice': 'Practice',
  'common.topicsOne': '{count} topic',
  'common.topicsMany': '{count} topics',

  // ── Auth hero (chalkboard panel, shared by login + register) ─────────
  'auth.heroHeadline': 'Unlock learning, one question at a time.',
  'auth.heroSubtext':
    'Adaptive practice, instant correction, and analytics that show every student exactly what to learn next.',
  'auth.heroFootnote': 'CBSE · Classes 7–10 · English & हिन्दी',

  // ── Login page ───────────────────────────────────────────────────────
  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to continue learning.',
  'login.username': 'Username',
  'login.usernamePlaceholder': 'Enter your username',
  'login.password': 'Password',
  'login.passwordPlaceholder': 'Enter your password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.error': 'Invalid username or password. Please try again.',
  'login.noAccount': "Don't have an account?",
  'login.registerLink': 'Register',
  'login.schoolQuestion': 'Are you a school?',
  'login.enquireLink': 'Enquire about OpenShiksha',

  // ── Home (marketing) page ────────────────────────────────────────────
  'home.login': 'Log in',
  'home.loginArrow': 'Log in →',
  'home.heroTitle': 'Unlock every child’s potential.',
  'home.heroSubtitle':
    'Adaptive learning and educational analytics for Maths & Science — free for students, and built for the teachers, parents, and schools who guide them.',
  'home.startFree': 'Start free as a student',
  'home.pillarPracticeTitle': 'Practice',
  'home.pillarPracticeDesc':
    'Unlimited, auto-generated questions in Maths & Science — no two students get the same paper.',
  'home.pillarEvaluateTitle': 'Evaluate',
  'home.pillarEvaluateDesc':
    'Every answer is corrected automatically and instantly — no repetitive marking for teachers.',
  'home.pillarAnalyseTitle': 'Analyse',
  'home.pillarAnalyseDesc':
    'Advanced analytics surface each child’s strengths, gaps, and what to practise next.',
  'home.missionKicker': 'Our mission',
  'home.missionTitle':
    'Make practising Maths & Science genuinely engaging — for every student.',
  'home.missionBody1':
    'OpenShiksha is a learning platform built to improve concept retention and learning outcomes. We cover classes 7–10 Maths and Science, with refreshers from class 1, all aligned to the CBSE board and available in English and Hindi.',
  'home.missionOpenPre': 'Our',
  'home.missionOpenTerm': 'Open Model',
  'home.missionOpenPost': 'lets any student sign up and learn for free. Our',
  'home.missionPartnerTerm': 'Partnership Model',
  'home.missionPartnerPost':
    'gives schools and educational organisations the dashboards to run virtual classrooms and make data-driven decisions.',
  'home.teacherAlt': 'A teacher helping students',
  'home.startNowTitle': 'Start now',
  'home.startNowSubtitle':
    'Free for students, forever. A guided onboarding for schools and educational organisations.',
  'home.studentsCardTitle': 'Students',
  'home.studentsCardDesc': 'Create a free account and start practising today.',
  'home.studentsCardCta': 'Sign up free →',
  'home.schoolsCardTitle': 'Schools & organisations',
  'home.schoolsCardDesc': 'Bring OpenShiksha to your classrooms.',
  'home.schoolsCardCta': 'Enquire →',
  'home.featuresTitle': 'Key features',
  'home.feature1':
    'Advanced analytics pinpoint strengths, weaknesses, and concepts that need attention.',
  'home.feature2': 'Personalised feedback targets each student’s specific learning outcomes.',
  'home.feature3':
    'Formulaic templates generate near-infinite questions — unlimited practice, no copying.',
  'home.feature4': 'Automated correction removes hours of repetitive marking for teachers.',
  'home.feature5':
    'A dedicated parent dashboard with weekly AI summaries keeps families engaged.',
  'home.feature6': 'Works beautifully on low-cost mobiles and tablets, in English and Hindi.',
  'home.footerSchools': 'For schools',
  'home.footerGithub': 'GitHub',

  // ── Registration pages ───────────────────────────────────────────────
  'register.title': 'Create your account',
  'register.subtitle': 'How would you like to learn?',
  'register.haveAccount': 'Already have an account?',
  'register.signIn': 'Sign in',
  'register.back': '← Back',
  'register.joinSchoolTitle': 'Join a school',
  'register.joinSchoolDesc':
    'Use a classroom join code from your teacher to enroll automatically.',
  'register.openTitle': 'Study independently',
  'register.openDesc':
    'Practice from the shared question bank at your own pace — no school needed.',
  'register.openSubtitle': 'Access the shared question bank for free.',
  'register.schoolTitle': 'Join your school',
  'register.schoolSubtitle': 'Enter the join code from your teacher.',
  'register.firstName': 'First name',
  'register.lastName': 'Last name',
  'register.username': 'Username',
  'register.password': 'Password',
  'register.emailOptional': 'Email (optional)',
  'register.joinCode': 'Classroom join code',
  'register.joinCodePlaceholder': 'e.g. ABC123',
  'register.error': 'Registration failed. Please try again.',
  'register.creating': 'Creating account…',
  'register.startPractising': 'Start practising',
  'register.createAccount': 'Create account',

  // ── Student dashboard ────────────────────────────────────────────────
  'dashboard.greeting': 'Hi, {name}!',
  'dashboard.title': 'Your dashboard',
  'dashboard.subtitle': 'Here are your assignments.',
  'dashboard.myProgress': 'My progress →',
  'dashboard.loadError': 'Failed to load assignments. Please refresh the page.',
  'dashboard.openEmptyTitle': "You're not enrolled in a classroom yet",
  'dashboard.openEmptyDescription':
    'Browse the shared question bank to start practising on your own.',
  'dashboard.browseSubjects': 'Browse subjects →',

  // ── Assignment list (sections + empty state) ─────────────────────────
  'assignments.emptyTitle': 'No assignments yet',
  'assignments.emptyDescription': 'Your teacher will assign some soon. Check back later!',
  'assignments.sectionOverdue': 'Overdue',
  'assignments.sectionDueSoon': 'Due Soon',
  'assignments.sectionUpcoming': 'Upcoming',
  'assignments.sectionCompleted': 'Completed',

  // ── Assignment card ──────────────────────────────────────────────────
  'assignment.overdueBy': 'Overdue by {distance}',
  'assignment.dueIn': 'Due {distance}',
  'assignment.submittedAgo': 'Submitted {distance}',
  'assignment.submitted': 'Submitted',
  'assignment.remedialBadge': 'Remedial Practice',
  'assignment.progress': 'Progress',
  'assignment.ctaReview': 'Review',
  'assignment.ctaContinue': 'Continue',
  'assignment.ctaStart': 'Start',

  // ── Assignment detail (work + submit flow) ───────────────────────────
  'assignmentDetail.back': 'Back to assignments',
  'assignmentDetail.answeredCount': '{answered} of {total} answered',
  'assignmentDetail.submittedNice': 'Assignment submitted — nice work!',
  'assignmentDetail.submittedTitle': 'Submitted',
  'assignmentDetail.grading': 'Grading in progress…',
  'assignmentDetail.submittedOfflineTitle': 'Submitted',
  'assignmentDetail.gradePending': "Will be graded when you're back online",
  'assignmentDetail.notFoundTitle': 'Assignment not found',
  'assignmentDetail.notFoundDescription': "It may have been removed or you don't have access.",
  'assignmentDetail.backToDashboard': 'Back to dashboard',
  'assignmentDetail.noQuestions': 'No questions in this assignment',
  'assignmentDetail.submit': 'Submit assignment',
  'assignmentDetail.confirmTitle': 'Submit assignment?',
  'assignmentDetail.confirmBody':
    'You have answered {answered} of {total} questions. You cannot change your answers after submitting.',
  'assignmentDetail.confirmSubmit': 'Submit',
  'assignmentDetail.submitting': 'Submitting…',

  // ── Due for Review panel (SRS) ───────────────────────────────────────
  'dueReview.title': 'Due for Review',
  'dueReview.overdueCount': '{count} overdue',
  'dueReview.statusOverdue': 'Overdue',
  'dueReview.statusToday': 'Due today',
  'dueReview.statusSoon': 'Coming up',
  'dueReview.overdueSince': 'Overdue since {date}',
  'dueReview.dueOn': 'Due {date}',
  'dueReview.interval': 'every {days}d',
  'dueReview.reviewedTimes': '{count}× reviewed',
  'dueReview.moreTopics': '+{count} more topics',
  'dueReview.emptyDescription':
    'Nothing due yet — finish a few assignments and your review schedule will appear here.',
  'dueReview.footer': 'Practice your assignments to push review dates forward.',

  // ── Recommendations panel ────────────────────────────────────────────
  'recommendations.title': 'What to Practice Next',
  'recommendations.todaysPlan': "Today's plan: ~{minutes} min",
  'recommendations.yourScore': 'your score',
  'recommendations.planTopicsOne': '{count} topic in today’s plan',
  'recommendations.planTopicsMany': '{count} topics in today’s plan',
  'recommendations.viewProgress': 'View progress →',
  'recommendations.viewLearningPath': 'View learning path →',
  'recommendations.emptyDescription':
    "No suggestions yet — answer a few assignment questions and we'll point you to the chapters worth revisiting.",

  // ── Explanation panel (AI) ───────────────────────────────────────────
  'explanation.cta': '✨ Explain this answer',
  'explanation.whyRight': 'Why this answer is right',
  'explanation.whereWrong': 'Where this went wrong',
  'explanation.writing': 'Writing your explanation…',
  'explanation.error': "Couldn't fetch an explanation just now. Please try again in a moment.",
  'explanation.retry': 'Retry',
  'explanation.stubNote':
    'Generated without an AI model — explanations get richer once AI is configured.',
  'explanation.regenerateInLocale': 'Explain in English',
  'explanation.regenerating': 'Rewriting…',

  // ── Parent dashboard ─────────────────────────────────────────────────
  'parent.title': 'Parent Dashboard',
  'parent.description': "Monitor your children's learning progress.",
  'parent.noChildrenTitle': 'No children linked to your account',
  'parent.noChildrenDescription': "Ask the school admin to link your children's accounts.",
  'parent.gradeShort': 'Gr.{grade}',
  'parent.grade': 'Grade {grade}',
  'parent.overview': "{name}'s Overview",
  'parent.viewInsights': 'View Insights →',
  'parent.tabProgress': 'Progress',
  'parent.tabAssignments': 'Assignments',
  'parent.noProgressTitle': 'No progress yet',
  'parent.noProgressDescription': "{name} hasn't submitted any assignments yet.",
  'parent.noAssignmentsDescription':
    'Assignments will appear here once the teacher creates them.',
  'parent.questionsPractisedOne': '{count} question practised',
  'parent.questionsPractisedMany': '{count} questions practised',
  'parent.statusSubmitted': 'Submitted',
  'parent.statusOverdue': 'Overdue',
  'parent.statusPending': 'Pending',
  'parent.overdueOn': 'Overdue — {date}',
  'parent.dueOn': 'Due {date}',

  // ── Teacher dashboard + insight panels ───────────────────────────────
  'teacher.title': 'Teacher dashboard',
  'teacher.subtitle': 'Your rooms, problem sets, and assignments in one place.',
  'teacher.aiGrading': '✨ AI grading',
  'teacher.newQuestion': '+ Question',
  'teacher.newProblemSet': '+ Problem set',
  'teacher.newAssignment': '+ Assignment',
  'teacher.statRooms': 'Subject rooms',
  'teacher.statStudents': 'Students',
  'teacher.statProblemSets': 'Problem sets',
  'teacher.statOpenAssignments': 'Open assignments',
  'teacher.noRoomsTitle': 'No subject rooms yet',
  'teacher.noRoomsDescription': 'Ask an admin to assign you to a classroom.',
  'teacher.studentsCountOne': '{count} student',
  'teacher.studentsCountMany': '{count} students',
  'teacher.assign': 'Assign',
  'teacher.viewInsights': 'View class insights',
  'teacher.hideInsights': 'Hide class insights',
  'teacher.newSetAction': '+ New set',
  'teacher.noSetsTitle': 'No problem sets yet',
  'teacher.noSetsDescription': 'Build a set of questions you can assign to any of your rooms.',
  'teacher.buildSet': 'Build a problem set',
  'teacher.questionsCountOne': '{count} question',
  'teacher.questionsCountMany': '{count} questions',
  'teacher.minutesApprox': '~{minutes} min',
  'teacher.previewAsStudent': 'Preview as student',
  'teacher.sectionJoinCodes': 'Class join codes',
  'teacher.joinCodesDescription': 'Share a code so students can join the classroom themselves.',
  'teacher.sectionAssignments': 'Assignments',
  'teacher.noAssignmentsDescription': 'Create your first assignment to start tracking submissions.',
  'teacher.createAssignment': 'Create assignment',
  'teacher.submittedRatio': '{submitted}/{total} submitted',
  'teacher.avgScore': 'Avg: {pct}%',

  // Needs-attention strip
  'teacher.needsAttention': 'Needs attention · {count}',
  'teacher.bucketOverdue': '{count} overdue',
  'teacher.bucketUngraded': '{count} ungraded',
  'teacher.bucketLowCompletion': '{count} low completion',

  // Class health panel
  'teacher.classHealth': 'Class Health',
  'teacher.loadingClassHealth': 'Loading class health data…',
  'teacher.noClassHealth':
    'No class health data yet. Insights appear after students complete assignments.',
  'teacher.computing': 'Computing…',
  'teacher.refresh': 'Refresh',
  'teacher.refreshing': 'Refreshing…',
  'teacher.thChapter': 'Chapter',
  'teacher.thAvg': 'Avg',
  'teacher.thStruggling': 'Struggling',
  'teacher.thStatus': 'Status',
  'teacher.statusStruggling': 'Struggling',
  'teacher.statusAtRisk': 'At Risk',
  'teacher.statusProficient': 'Proficient',
  'teacher.lastUpdated': 'Last updated: {datetime}',

  // Assignment detail page (RML-2)
  'teacher.adBackToDashboard': 'Back to dashboard',
  'teacher.adLoadFailTitle': 'Failed to load assignment',
  'teacher.adLoadFailDesc': 'It may not exist or you may not have access.',
  'teacher.adStatDue': 'Due',
  'teacher.adStatSubmitted': 'Submitted',
  'teacher.adStatAvg': 'Avg score',
  'teacher.adOverdue': 'Overdue',
  'teacher.adActive': 'Active',
  'teacher.adSubmittedPct': '{pct}% submitted',
  'teacher.adSubmissionsTitle': 'Student Submissions',
  'teacher.adSubmissionsRecordedOne': '{count} submission recorded',
  'teacher.adSubmissionsRecordedMany': '{count} submissions recorded',
  'teacher.adNotStarted': '{count} students have not started',
  'teacher.adNoSubmissions': 'No submissions yet.',
  'teacher.adThStudent': 'Student',
  'teacher.adThSubmitted': 'Submitted',
  'teacher.adThScore': 'Score',
  'teacher.adThStatus': 'Status',
  'teacher.adBadgeSubmitted': 'Submitted',
  'teacher.adBadgePending': 'Pending',
  'teacher.adGrading': 'Grading…',
  'teacher.adHardestTitle': 'Hardest Questions',
  'teacher.adHardestSub': '(by cumulative marks lost)',
  'teacher.adPtsLost': '{pts} pts lost',

  // Weekly AI summary panel
  'teacher.weeklySummary': 'Weekly AI Summary',
  'teacher.weeklyLoadError': "Couldn't load the weekly summary just now.",
  'teacher.noWeeklySummary': "No weekly summary yet. Generate one from this week's practice activity.",
  'teacher.generating': 'Generating…',
  'teacher.generate': 'Generate',
  'teacher.weeklyGenError': "Couldn't generate the summary just now. Please try again in a moment.",
  'teacher.weekOf': 'Week of {start} – {end}',
  'teacher.stubSummaryNote': 'AI was unavailable, so this was built directly from your class data.',
  'teacher.practisedRatio': '{active}/{total} practised',
  'teacher.avgPct': '{pct}% avg',
  'teacher.needsWork': 'Needs work:',
  'teacher.strong': 'Strong:',
  'teacher.generatedOn': 'Generated {date}',
  'teacher.regenerating': 'Regenerating…',
  'teacher.regenerate': 'Regenerate',

  // Interventions panel
  'teacher.interventions': 'Intervention Suggestions',
  'teacher.weakChaptersOne': '{count} weak chapter',
  'teacher.weakChaptersMany': '{count} weak chapters',
  'teacher.priority': 'Priority {n}',
  'teacher.stubStrategyNote':
    "AI was unavailable, so this strategy was built directly from {name}'s learning-gap data.",
  'teacher.recurringMisconception': 'Recurring misconception:',
  'teacher.dismiss': 'Dismiss',
  'teacher.markPlanned': 'Mark as planned',
  'teacher.planned': 'Planned',
  'teacher.dismissed': 'Dismissed',
  'teacher.resolve': 'Resolve',
  'teacher.interventionsLoadError': "Couldn't load suggestions just now.",
  'teacher.noInterventions':
    'No struggling students flagged yet. Generate suggestions from current learning-gap data.',
  'teacher.interventionsGenError':
    "Couldn't generate suggestions just now. Please try again in a moment.",
  'teacher.interventionsFootnote': 'AI strategies guide your follow-up — you stay in control.',

  // Misconception clusters panel
  'teacher.misconceptions': 'Class Misconceptions',
  'teacher.tryInClass': 'Try in class:',
  'teacher.seenTimesOne': 'Seen {count} time · last on {last} · updated {updated}',
  'teacher.seenTimesMany': 'Seen {count} times · last on {last} · updated {updated}',
  'teacher.misconceptionsLoadError': "Couldn't load misconception patterns just now.",
  'teacher.noMisconceptions':
    'No misconception patterns detected yet — they appear once students have a few graded assignments in this class.',
  'teacher.refreshError': "Couldn't refresh just now. Please try again in a moment.",
  'teacher.recomputing': 'Recomputing from recent submissions — new patterns appear here shortly.',
  'teacher.misconceptionsFootnote':
    'Patterns across the whole class — address them once, help everyone.',

  // AI assignment drafts panel
  'teacher.aiDrafts': 'AI Assignment Drafts',
  'teacher.draftPending': "Assembling a draft from your class's weak spots…",
  'teacher.draftPendingNote': 'This usually takes a few seconds.',
  'teacher.draftFailed': "Couldn't put this draft together.",
  'teacher.retrying': 'Retrying…',
  'teacher.tryAgain': 'Try again',
  'teacher.practiceSetDefault': 'Practice set',
  'teacher.assigned': 'Assigned',
  'teacher.viewAssignment': 'View the assignment',
  'teacher.classCanSee': 'your class can see it now.',
  'teacher.stubDraftNote':
    "AI was unavailable, so this draft was built directly from your class's practice data.",
  'teacher.dismissDraftError': "Couldn't dismiss the draft just now. Please try again in a moment.",
  'teacher.approveEllipsis': 'Approve…',
  'teacher.dueDate': 'Due date',
  'teacher.titleLabel': 'Title',
  'teacher.titleOptionalHint': "(optional — keeps the draft's title if blank)",
  'teacher.approveError': "Couldn't approve the draft just now. Please try again in a moment.",
  'teacher.useTryAgainHint': 'Use "Try again" to build a fresh draft.',
  'teacher.assigning': 'Assigning…',
  'teacher.assignToClass': 'Assign to class',
  'teacher.draftsLoadError': "Couldn't load drafts just now.",
  'teacher.noDrafts':
    "No drafts yet — let AI assemble a practice set from this class's weakest chapters, then review and assign it.",
  'teacher.draftStartError': "Couldn't start a draft just now. Please try again in a moment.",
  'teacher.questionsLabel': 'Questions',
  'teacher.difficultyLabel': 'Difficulty',
  'teacher.starting': 'Starting…',
  'teacher.draftAssignment': 'Draft an assignment',
  'teacher.aiProposesFootnote':
    'AI proposes, you decide — nothing reaches students until you approve it.',

  // Question quality panel
  'teacher.questionQuality': 'Question Quality',
  'teacher.flagTooHard': 'Almost no one got this right — check the wording or the keyed answer.',
  'teacher.flagTooEasy': 'Almost everyone got this right — it adds little to the set.',
  'teacher.flagMislabeled': 'Behaves harder or easier than its authored difficulty label.',
  'teacher.flagLowDiscrimination':
    'Stronger students did no better than weaker ones — often a mis-keyed answer.',
  'teacher.flagOk': 'Behaves as authored.',
  'teacher.questionFallback': 'Question #{id}',
  'teacher.correctPct': '{pct} correct',
  'teacher.authoredDifficulty': 'Authored difficulty',
  'teacher.observedDifficulty': 'Observed difficulty',
  'teacher.harderThanLabelled': 'Harder than labelled',
  'teacher.easierThanLabelled': 'Easier than labelled',
  'teacher.discrimination': 'Discrimination',
  'teacher.calibrationSummaryOne': '{flagged} of {total} calibrated question need a look.',
  'teacher.calibrationSummaryMany': '{flagged} of {total} calibrated questions need a look.',
  'teacher.qualityLoadError': "Couldn't load the question analysis just now.",
  'teacher.noFlagged':
    'No questions flagged yet. Calibration needs a handful of student attempts per question — refresh once your class has practised.',
  'teacher.recalibrateStartError':
    "Couldn't start the recalibration just now. Please try again in a moment.",
  'teacher.recalibrating':
    'Recalibrating from recent answers — updated verdicts appear here shortly.',
  'teacher.qualityFootnote':
    'Verdicts come from how your class actually answered — no AI guesswork.',
  'teacher.calibrate': 'Calibrate',

  // Classroom join-code widget
  'teacher.joinCodeHeading': 'Classroom Join Code',
  'teacher.copied': 'Copied!',
  'teacher.copyCode': 'Copy code',
  'teacher.copyLink': 'Copy link',
  'teacher.noExpiry': 'No expiry',
  'teacher.expired': 'Expired',
  'teacher.expiresInUnderHour': 'Expires in <1 hour',
  'teacher.expiresInHours': 'Expires in {hours} hours',
  'teacher.expiresInDays': 'Expires in {days} days',
  'teacher.regenerateWarning':
    "Regenerating invalidates the current code. Students who haven't joined yet will need the new one.",
  'teacher.yesRegenerate': 'Yes, regenerate',
  'teacher.generateJoinCode': 'Generate Join Code',

  // ── Create-assignment page ───────────────────────────────────────────
  'assignForm.eyebrow': 'Authoring',
  'assignForm.title': 'Create Assignment',
  'assignForm.description':
    'Assign a problem set to one of your subject rooms. Students see it instantly.',
  'assignForm.back': '← Back',
  'assignForm.stepClass': 'Choose class',
  'assignForm.noRooms': "You don't teach any subject rooms yet.",
  'assignForm.subjectRoomLabel': 'Subject room',
  'assignForm.selectClass': 'Select a class…',
  'assignForm.stepProblemSet': 'Pick problem set',
  'assignForm.chooseClassFirst': 'Choose a class first.',
  'assignForm.noSetsForSubject': 'No problem sets for {subject} yet.',
  'assignForm.buildOne': '+ Build one',
  'assignForm.stepDueDate': 'Set due date',
  'assignForm.dueDateLabel': 'Due date',
  'assignForm.quickSet': 'Quick set:',
  'assignForm.preset3days': '3 days',
  'assignForm.preset1week': '1 week',
  'assignForm.preset2weeks': '2 weeks',
  'assignForm.dueTodayHint': 'Due today · {date}',
  'assignForm.dueInDaysHintOne': 'Due in {count} day · {date}',
  'assignForm.dueInDaysHintMany': 'Due in {count} days · {date}',
  'assignForm.successTitle': 'Assignment created!',
  'assignForm.successBodyOne':
    '{count} student in {room} can now see it on their dashboard. Due {date}.',
  'assignForm.successBodyMany':
    '{count} students in {room} can now see it on their dashboard. Due {date}.',
  'assignForm.backToDashboard': 'Back to dashboard',
  'assignForm.createAnother': 'Create another',
  'assignForm.previewEmptyTitle': 'Your assignment preview',
  'assignForm.previewEmptyBody':
    "Pick a class, a problem set, and a due date — you'll see exactly what students see here before you publish.",
  'assignForm.previewEyebrow': 'Assignment preview',
  'assignForm.pickProblemSet': 'Pick a problem set',
  'assignForm.whatStudentsSee': 'What students will see',
  'assignForm.pickSetLeft': 'Pick a problem set on the left.',
  'assignForm.previewQuestionsLink': 'Preview the actual questions as a student',
  'assignForm.dueLabel': 'Due',
  'assignForm.noDueYet': 'No due date set yet',
  'assignForm.dueOn': 'Due {date}',
  'assignForm.dueOnTodaySuffix': 'Due {date} · today',
  'assignForm.dueOnDaysSuffixOne': 'Due {date} · {count} day from now',
  'assignForm.dueOnDaysSuffixMany': 'Due {date} · {count} days from now',
  'assignForm.recipients': 'Recipients',
  'assignForm.recipientsValueOne': '{count} student in {room}',
  'assignForm.recipientsValueMany': '{count} students in {room}',
  'assignForm.completeAllSteps': 'Complete all three steps to publish.',
  'assignForm.readyToPublishOne': 'Ready to publish to {count} student.',
  'assignForm.readyToPublishMany': 'Ready to publish to {count} students.',
  'assignForm.createFailed': 'Failed to create. Please try again.',
  'assignForm.publish': 'Publish Assignment',
  'assignForm.publishing': 'Publishing…',

  // ── Build-problem-set page ───────────────────────────────────────────
  'setForm.title': 'Build Problem Set',
  'setForm.description':
    'Group questions into an assignable set. Click a question to preview exactly how students will see it.',
  'setForm.details': 'Details',
  'setForm.titlePlaceholder': 'e.g. Quadratic Equations Practice Set 1',
  'setForm.subject': 'Subject',
  'setForm.selectSubject': 'Select subject…',
  'setForm.chapter': 'Chapter',
  'setForm.selectChapter': 'Select chapter…',
  'setForm.chapterOption': '{name} (Std {std})',
  'setForm.estimatedTime': 'Estimated time (min)',
  'setForm.estimatedPlaceholder': 'e.g. 30',
  'setForm.selectQuestions': 'Select Questions',
  'setForm.filteredOfTotal': '{filtered} of {total}',
  'setForm.searchPlaceholder': 'Search question text…',
  'setForm.difficultyFilter': 'Difficulty:',
  'setForm.any': 'Any',
  'setForm.typeFilter': 'Type:',
  'setForm.selectSubjectToBrowse': 'Select a subject to browse questions.',
  'setForm.noQuestions': 'No questions found.',
  'setForm.authorOne': 'Author one →',
  'setForm.noMatches': 'No matches for the current filters.',
  'setForm.previewEmptyTitle': 'Pick a question to preview',
  'setForm.previewEmptyDesc':
    'Click any row on the left to see exactly how students will see it — full LaTeX, options, images, and all.',
  'setForm.inSet': 'This question is in the set.',
  'setForm.notInSet': 'Not in the set yet.',
  'setForm.removeFromSet': 'Remove from set',
  'setForm.addToSet': '+ Add to set',
  'setForm.selectQuestionAria': 'Select question {id}',
  'setForm.removeQuestionAria': 'Remove question {id}',
  'setForm.difficultyTitle': 'Difficulty {n}/5',
  'setForm.partsCount': '{count} parts',
  'setForm.interactive': 'Interactive',
  'setForm.questionsSelectedOne': 'question selected',
  'setForm.questionsSelectedMany': 'questions selected',
  'setForm.successTitle': 'Problem set created!',
  'setForm.successBodyOne': '{title} with {count} question is ready to assign.',
  'setForm.successBodyMany': '{title} with {count} questions is ready to assign.',
  'setForm.assignNow': 'Assign it now',
  'setForm.backToBank': 'Back to question bank',
  'setForm.fillAllDesktop':
    'Fill in title, subject, chapter, and select at least one question.',
  'setForm.fillAllMobile': 'Pick a subject, chapter, title, and at least one question.',
  'setForm.create': 'Create Problem Set',
  'setForm.createMobile': 'Create',
  'setForm.creating': 'Creating…',

  // ── Open-response grading queue ──────────────────────────────────────
  'grading.title': 'AI grading',
  'grading.backDashboard': '← Dashboard',
  'grading.subtitle':
    'Open-ended answers, graded by AI, finalised by you. Nothing counts until you review it.',
  'grading.filterByClass': 'Filter by class',
  'grading.allMyClasses': 'All my classes',
  'grading.roomOption': '{subject} · {classroom}',
  'grading.filterAll': 'All',
  'grading.filterNeedsReview': 'Needs review',
  'grading.filterAiGrading': 'AI grading',
  'grading.filterFinalised': 'Finalised',
  'grading.filterFailed': 'Failed',
  'grading.statusPending': 'AI grading…',
  'grading.statusAiGraded': 'Needs your review',
  'grading.statusReviewed': 'Finalised',
  'grading.statusFailed': 'Grading failed',
  'grading.cardMeta': '{subject} · submitted {date}',
  'grading.questionLabel': 'Question:',
  'grading.aiReading': 'AI is reading this response…',
  'grading.gradeFailed': "Couldn't grade this response.",
  'grading.retrying': 'Retrying…',
  'grading.aiSuggests': 'AI suggests {score}/{max}',
  'grading.confident': '{pct}% confident',
  'grading.lowConfidenceNote':
    "The AI is unsure about this one — read the response and double-check before accepting.",
  'grading.autoGraded': 'Auto-graded',
  'grading.stubNote':
    'AI was unavailable, so this suggestion came from a keyword match against the model answer — please review with extra care.',
  'grading.regrading': 'Re-grading…',
  'grading.askAiAgain': 'Ask AI again',
  'grading.finalGrade': 'Final grade: {score}/{max}',
  'grading.aiSuggestedOverride': 'AI suggested {score}/{max} — you overrode it.',
  'grading.yourComment': 'Your comment:',
  'grading.finalMarks': 'Final marks (out of {max})',
  'grading.commentLabel': 'Comment for the student',
  'grading.optional': '(optional)',
  'grading.commentPlaceholder': 'e.g. Good reasoning — name the process next time.',
  'grading.saveError': "Couldn't save the grade just now. Please try again in a moment.",
  'grading.saving': 'Saving…',
  'grading.acceptSuggestion': 'Accept suggestion',
  'grading.saveFinal': 'Save final grade',
  'grading.finaliseLocks': 'Finalising locks this grade.',
  'grading.loadError': "Couldn't load the grading queue just now.",
  'grading.retry': 'Retry',
  'grading.emptyFilteredTitle': 'Nothing matches these filters',
  'grading.emptyTitle': 'No responses to grade yet',
  'grading.emptyFilteredDesc': 'Try a different class or status.',
  'grading.emptyDesc':
    'Record a student’s open-ended answer and AI will suggest a grade for your review.',

  // ── LA-6e-2: question-type labels (shared) ───────────────────────────
  'qtype.mcq': 'MCQ',
  'qtype.numeric': 'Numeric',
  'qtype.fill_blank': 'Fill blank',
  'qtype.multi_select': 'Multi-select',
  'qtype.matching': 'Matching',
  'qtype.compound': 'Compound',

  // ── LA-6e-2: question bank page ──────────────────────────────────────
  'qbank.eyebrow': 'Authoring',
  'qbank.title': 'Question Bank',
  'qbank.description':
    'Browse and reuse questions across your assignments. Click a question to preview exactly how students will see it.',
  'qbank.createQuestion': '+ Create Question',
  'qbank.searchPlaceholder': 'Search question text, subject, chapter…',
  'qbank.allSubjects': 'All subjects',
  'qbank.anyDifficulty': 'Any difficulty',
  'qbank.difficultyOption': '{stars} ({level}/5)',
  'qbank.allChapters': 'All chapters',
  'qbank.gradeSuffix': 'Grade {grade}',
  'qbank.pickSubjectFirst': 'Pick a subject first',
  'qbank.clearFilters': 'Clear filters',
  'qbank.noQuestionsFound': 'No questions found',
  'qbank.adjustFilters': 'Try adjusting filters.',
  'qbank.authorFirst': 'Author your first question.',
  'qbank.createAQuestion': 'Create a question →',
  'qbank.resultsCountOne': '{count} question',
  'qbank.resultsCountMany': '{count} questions',
  'qbank.difficultyTitle': 'Difficulty {level}/5',
  'qbank.rowEdit': 'Edit',
  'qbank.previewEmptyTitle': 'Pick a question to preview',
  'qbank.previewEmptyDesc':
    'Click any row on the left to see exactly how students will see it — full LaTeX, options, images, and all.',
  'qbank.reuseNote': 'Reuse this question in any of your problem sets.',
  'qbank.useInNewSet': 'Use in new set',
  'qbank.addToProblemSet': '+ Add to problem set',

  // ── LA-6e-2: add-to-problem-set sheet ────────────────────────────────
  'addToSet.eyebrow': 'Add to set',
  'addToSet.questionTitle': 'Question #{id}',
  'addToSet.closeLabel': 'Close add-to-set dialog',
  'addToSet.addedTitle': 'Added!',
  'addToSet.addedDesc': 'The question is now in the problem set.',
  'addToSet.done': 'Done',
  'addToSet.addAnother': 'Add to another',
  'addToSet.noSets': 'No problem sets in {subject} yet.',
  'addToSet.thisSubject': 'this subject',
  'addToSet.buildOne': 'Build one →',
  'addToSet.questionsCountOne': '{count} question',
  'addToSet.questionsCountMany': '{count} questions',
  'addToSet.failedToAdd': 'Failed to add. Please try again.',
  'addToSet.add': 'Add',

  // ── LA-6e-2: question preview panel ──────────────────────────────────
  'qpreview.part': 'Part {letter}',
  'qpreview.diagramAlt': 'Question diagram',
  'qpreview.numericAnswer': 'Numeric answer',
  'qpreview.fillBlankResponse': 'Fill-in-the-blank response.',
  'qpreview.interactiveWidget': '🧪 Sandboxed interactive widget (rendered for students)',
  'qpreview.emptyTitle': 'Pick a question to preview',
  'qpreview.emptyDesc':
    'Click any row on the left to see exactly how students will see it — full LaTeX, options, images, and all.',
  'qpreview.difficultyTitle': 'Difficulty {level}/5',

  // ── LA-6e-2: widget gallery panel ────────────────────────────────────
  'widgetGallery.pickTitle': 'Pick an interactive widget',
  'widgetGallery.pickDesc':
    'The widget renders inside a sandboxed iframe alongside the question text. Pick one to configure — the live preview updates as you fill in the form.',
  'widgetGallery.noneRegistered': 'No teacher-visible widgets are registered yet.',
  'widgetGallery.answerBadge': 'answer',
  'widgetGallery.noSchema': "This widget didn't declare a JSON Schema. Edit the raw config below.",
  'widgetGallery.backToGallery': '← back to gallery',
  'widgetGallery.config': 'Config',
  'widgetGallery.preview': 'Preview',
  'widgetGallery.previewNote':
    "Preview re-renders on every config change. Sandboxed iframe — scripts inside the widget can't reach the page.",
  'widgetGallery.useThis': 'Use this widget',

  // ── DTB-3: describe-to-build (AI widget authoring) ───────────────────
  'widgetGallery.describeTitle': 'Describe it — AI builds the widget',
  'widgetGallery.describeDesc':
    'Type what you want in plain English. AI proposes a validated, sandboxed widget you can preview, edit, and attach — the grader stays deterministic.',
  'widgetGallery.describePlaceholder': 'e.g. a number line where students mark 3/4',
  'widgetGallery.describeButton': 'Generate widget',
  'widgetGallery.describeGenerating': 'Building your widget…',
  'widgetGallery.describeError':
    "Couldn't reach the widget builder. Try again, or pick a widget below.",
  'widgetGallery.describeFallback':
    "AI is unavailable right now — here's a safe starter you can edit and attach.",
  'widgetGallery.describeOrPick': 'or pick one yourself',
  'widgetGallery.aiStubLabel': 'Auto-built',
  // ── DTB-5b: per-student randomisation toggle ─────────────────────────
  'widgetGallery.randomizeLabel': 'Each student gets different numbers',
  'widgetGallery.randomizeHint':
    'AI binds values to per-student variables so every learner sees a fresh problem. The grader stays deterministic.',
  'widgetGallery.randomizeBadge': '🎲 Randomized per student',
  'widgetGallery.randomizeNote': 'Each student gets fresh values for:',

  // ── LA-6e-2: record-response / rubric panel ──────────────────────────
  'recordResp.maxMarks': 'Maximum marks',
  'recordResp.modelAnswer': 'Model answer',
  'recordResp.modelAnswerHint': '(what a full-marks answer says)',
  'recordResp.modelAnswerPlaceholder':
    'e.g. Chlorophyll absorbs red and blue light for photosynthesis and reflects green.',
  'recordResp.markingPoints': 'Marking points',
  'recordResp.markingPointsHint': '(optional — enables per-point partial credit)',
  'recordResp.criterionLabelPlaceholder': 'e.g. Names chlorophyll',
  'recordResp.criterionLabelAria': 'Criterion {n} label',
  'recordResp.criterionMarksAria': 'Criterion {n} marks',
  'recordResp.removeCriterionAria': 'Remove criterion {n}',
  'recordResp.addMarkingPoint': '+ Marking point',
  'recordResp.sumMismatch':
    'Marking points add up to {sum}, not {max} — the AI grades per point, so consider matching the total.',
  'recordResp.saveError': "Couldn't save the rubric just now. Please try again in a moment.",
  'recordResp.saving': 'Saving…',
  'recordResp.updateRubric': 'Update rubric',
  'recordResp.saveRubric': 'Save rubric',
  'recordResp.noRubricWarning':
    'No rubric yet — without one the AI falls back to a rough keyword match. Add one for fair, transparent marks.',
  'recordResp.addRubric': 'Add rubric',
  'recordResp.rubricLabel': 'Rubric:',
  'recordResp.marksLabel': '{count} marks',
  'recordResp.markingPointsCountOne': '{count} marking point',
  'recordResp.markingPointsCountMany': '{count} marking points',
  'recordResp.edit': 'Edit',
  'recordResp.modelAnswerLabel': 'Model answer:',
  'recordResp.recordForAI': 'Record a response for AI grading',
  'recordResp.class': 'Class',
  'recordResp.pickClass': 'Pick a class…',
  'recordResp.student': 'Student',
  'recordResp.pickClassFirst': 'Pick a class first',
  'recordResp.loadingStudents': 'Loading students…',
  'recordResp.pickStudent': 'Pick a student…',
  'recordResp.shortAnswerQuestion': 'Short-answer question',
  'recordResp.loadingQuestions': 'Loading questions…',
  'recordResp.pickQuestion': 'Pick a question…',
  'recordResp.noShortAnswer':
    'No short-answer questions yet — create one in the question bank first; only short-answer questions can be AI-graded here.',
  'recordResp.studentAnswer': "Student's answer",
  'recordResp.studentAnswerPlaceholder': "Paste or type the student's written answer…",
  'recordResp.queueError': "Couldn't queue this response just now. Please try again in a moment.",
  'recordResp.queuedSuccess':
    'Queued — it appears below as "AI grading…" and flips to a suggestion in a few seconds.',
  'recordResp.aiSuggestsNote': 'AI suggests, you finalise — always.',
  'recordResp.queuing': 'Queuing…',
  'recordResp.sendToAI': 'Send to AI grading',

  // ── LA-6e-1: edit-safety banner ──────────────────────────────────────
  'editSafety.usedQuestionOne': 'This question is used in 1 assignment.',
  'editSafety.usedQuestionMany': 'This question is used in {count} assignments.',
  'editSafety.usedSetOne': 'This problem set is used in 1 assignment.',
  'editSafety.usedSetMany': 'This problem set is used in {count} assignments.',
  'editSafety.body':
    'Your edits apply to future assignments only — existing ones keep exactly what students were given.',
  'editSafety.bodyGraded':
    'Your edits apply to future assignments only — existing ones keep exactly what students were given and were graded against.',

  // ── LA-6e-1: assignment snapshot preview ─────────────────────────────
  'snapshot.heading': 'What students see · snapshot',
  'snapshot.frozenNote': "Frozen at assign time — editing the set later doesn't change this view.",
  'snapshot.hide': 'Hide',
  'snapshot.show': 'Show',
  'snapshot.driftTitle': 'The live set has changed since this assignment was given.',
  'snapshot.driftBody':
    'What students see and how this assignment grades come from the frozen snapshot above — newer assignments will use the updated set.',
  'snapshot.driftCompareLink': 'Compare with the live set',
  'snapshot.updateThis': 'Update this assignment…',
  'snapshot.previewBeforeApply': "We'll show what changes before anything is applied.",
  'snapshot.historyNote': 'This assignment was updated; an earlier snapshot is kept in history.',
  'snapshot.undoLast': 'Undo last update',
  'snapshot.undoing': 'Undoing…',
  'snapshot.empty': "This assignment's snapshot is empty.",

  // ── LA-6e-1: re-sync assignment modal ────────────────────────────────
  'resync.title': 'Update this assignment to the latest content?',
  'resync.subtitle': 'Re-snapshots from the live set. Reversible from this page.',
  'resync.loadError': "Couldn't load the preview. Try again.",
  'resync.noDrift': "The snapshot already matches the live set — there's nothing to re-sync.",
  'resync.regradeOne':
    '{count} graded submission will be re-graded against the new answer key. Some students may end up with different scores.',
  'resync.regradeMany':
    '{count} graded submissions will be re-graded against the new answer key. Some students may end up with different scores.',
  'resync.noRegrade': 'No graded submissions will be re-graded — only what students see is changing.',
  'resync.applyError': 'The re-sync failed. Try again.',
  'resync.updating': 'Updating…',
  'resync.updateAndRegrade': 'Update and re-grade {count}',
  'resync.updateAssignment': 'Update assignment',
  'resync.diffAddedOne': '{count} question added',
  'resync.diffAddedMany': '{count} questions added',
  'resync.diffRemovedOne': '{count} question removed',
  'resync.diffRemovedMany': '{count} questions removed',
  'resync.diffAnswerOne': '{count} answer changed',
  'resync.diffAnswerMany': '{count} answers changed',
  'resync.diffContentOne': '{count} other content edit (prompts, options, widgets)',
  'resync.diffContentMany': '{count} other content edits (prompts, options, widgets)',

  // ── LA-6e-1: problem-set preview page ────────────────────────────────
  'psPreview.loadErrorTitle': "Couldn't load this preview",
  'psPreview.loadErrorDesc':
    'The problem set may have been removed, or you may not have access to it.',
  'psPreview.backToDashboard': 'Back to dashboard',
  'psPreview.confirmRemoveAssigned':
    'Remove this question from the live set?\n\nExisting assignments keep it — they grade and render the frozen copy.',
  'psPreview.confirmRemove': 'Remove this question from the set?',
  'psPreview.editingTitle': 'Editing set · live changes',
  'psPreview.readOnlyTitle': 'Student preview · read-only',
  'psPreview.editingBody':
    'Changes apply to future assignments only — existing ones keep what students were given.',
  'psPreview.readOnlyBody':
    "This is exactly how students see the set — answers hidden, variables filled in. You can't type or submit here.",
  'psPreview.doneEditing': 'Done editing',
  'psPreview.editSet': 'Edit set',
  'psPreview.back': '← Back',
  'psPreview.questionsOne': '{count} question',
  'psPreview.questionsMany': '{count} questions',
  'psPreview.emptyTitle': 'This set has no questions yet',
  'psPreview.emptyEditingDesc': 'Use the Add question button below to start filling it in.',
  'psPreview.emptyDesc': 'Add questions to the set, then preview again.',
  'psPreview.editQuestion': 'Edit question',
  'psPreview.removeFromSet': 'Remove from set',
  'psPreview.addMoreTitle': 'Add more questions',
  'psPreview.addMoreDesc':
    "Pick from the question bank, or author a new question — you'll come back here when done.",
  'psPreview.addQuestion': 'Add question',
  'psPreview.viewVersions': 'View version history',

  // ── LA-6e-4: create / edit question page ─────────────────────────────
  // Widget picker section
  'cqp.widgetLabel': 'Interactive widget',
  'cqp.widgetLabelHint': '(optional — renders in a sandbox)',
  'cqp.fieldsConfiguredOne': '{count} field configured',
  'cqp.fieldsConfiguredMany': '{count} fields configured',
  'cqp.edit': 'Edit',
  'cqp.remove': 'Remove',
  'cqp.addWidget': '+ Add interactive widget',
  // Live preview
  'cqp.previewPlaceholder': 'Type question text above to see preview...',
  'cqp.preview': 'Preview',
  // Variable preview
  'cqp.varPreviewStudentA': 'Student A: {text}',
  'cqp.varPreviewStudentB': 'Student B: {text}',
  // Draft card
  'cqp.draftBadge': 'Draft',
  'cqp.useThis': 'Use this',
  'cqp.draftAnswer': 'Answer:',
  // AI generation panel
  'cqp.generateWithAI': 'Generate with AI',
  'cqp.generateSubtitle': 'Draft questions from your topic and constraints',
  'cqp.topicLabel': 'Topic or concept to test',
  'cqp.topicPlaceholder': 'e.g. Factoring quadratic polynomials, laws of thermodynamics…',
  'cqp.typeLabelShort': 'Type',
  'cqp.countLabel': 'Count',
  'cqp.typeMcq': 'MCQ',
  'cqp.typeNumeric': 'Numeric',
  'cqp.typeFillBlank': 'Fill blank',
  'cqp.typeMultiSelect': 'Multi-select',
  'cqp.generating': 'Generating…',
  'cqp.generate': '✨ Generate',
  'cqp.generationFailed': 'Generation failed.',
  'cqp.retry': 'Retry',
  'cqp.aiUnavailable':
    'AI question generation is unavailable right now. Please try again in a little while, or write your question by hand below.',
  'cqp.draftsCountOne': '{count} draft — click "Use this" to pre-fill the form',
  'cqp.draftsCountMany': '{count} drafts — click "Use this" to pre-fill the form',
  // Page header
  'cqp.titleEdit': 'Edit Question',
  'cqp.titleCreate': 'Create Question',
  'cqp.subtitleEdit': 'Update this question in the shared question bank.',
  'cqp.subtitleCreate': 'Add a question to the shared question bank.',
  'cqp.back': '← Back',
  // Success state
  'cqp.successUpdated': 'Question updated!',
  'cqp.successCreated': 'Question created!',
  'cqp.successBodyUpdated': 'Question #{id} has been updated in the question bank.',
  'cqp.successBodyCreated': 'Question #{id} has been added to the question bank.',
  'cqp.createAnother': 'Create another',
  'cqp.backToBank': 'Back to question bank',
  // Chapter card
  'cqp.chapter': 'Chapter',
  'cqp.subject': 'Subject',
  'cqp.selectSubject': 'Select subject...',
  'cqp.selectChapter': 'Select chapter...',
  'cqp.chapterOption': '{name} (Std {std})',
  'cqp.difficulty': 'Difficulty',
  'cqp.difficultyHint': '1 = easiest, 5 = hardest',
  // Subpart tabs
  'cqp.partTab': 'Part {letter}',
  'cqp.addPart': '+ Add part',
  // Question fields
  'cqp.questionType': 'Question type',
  'cqp.typeMcqLong': 'Multiple choice (MCQ)',
  'cqp.typeFillBlankLong': 'Fill in the blank',
  'cqp.questionText': 'Question text',
  'cqp.latexHint': '(LaTeX: $x^2$ or $$\\frac{a}{b}$$)',
  'cqp.variableTokenHint': ' · use {{a}} for variable tokens',
  'cqp.questionTextPlaceholderVar': 'e.g. Solve ${{a}}x + {{b}} = {{c}}$',
  'cqp.questionTextPlaceholder': 'e.g. Solve $x^2 - 4 = 0$',
  'cqp.imageUrl': 'Image URL',
  'cqp.imageUrlHint': '(optional — shown above question text)',
  'cqp.imageUrlPlaceholder': 'Paste an image URL or upload a file',
  'cqp.uploading': 'Uploading...',
  'cqp.uploadImage': 'Upload image',
  'cqp.imageFormats': 'PNG, JPG, GIF, or WebP under 5 MB.',
  'cqp.imageUploadFailed': 'Image upload failed. Try a PNG, JPG, GIF, or WebP under 5 MB.',
  'cqp.imagePreviewAlt': 'Preview',
  'cqp.solution': 'Worked solution',
  'cqp.solutionHint': '(optional — shown to students after grading)',
  'cqp.solutionPlaceholder': 'Step-by-step solution. LaTeX with $...$ supported.',
  'cqp.hint': 'Hint',
  'cqp.hintHint': '(optional — shown to struggling students during practice)',
  'cqp.hintPlaceholder': 'A nudge in the right direction.',
  // Variable constraints
  'cqp.varConstraints': 'Variable Constraints',
  'cqp.varConstraintsHint': '— define the range for each {{token}} in your question',
  'cqp.min': 'min',
  'cqp.max': 'max',
  'cqp.integer': 'Integer',
  // MCQ options + answer
  'cqp.options': 'Options',
  'cqp.optionPlaceholder': 'Option {key}',
  'cqp.correctAnswer': 'Correct answer',
  'cqp.correctAnswerTokenHint': '(can use {{tokens}}, e.g. ({{c}} - {{b}}) / {{a}})',
  'cqp.selectCorrectOption': 'Select correct option...',
  'cqp.correctNumericPlaceholder': 'e.g. 42',
  'cqp.correctAnswerPlaceholder': 'Correct answer',
  'cqp.removePart': 'Remove this part',
  // Submit
  'cqp.updateQuestion': 'Update question',
  'cqp.saveQuestion': 'Save question',
  'cqp.cannotSubmitHint': 'Select a chapter and fill in all question text to save.',
  'cqp.saveFailed': 'Failed to save. Please try again.',

  // ── LA-6e-3: problem-set version history ─────────────────────────────
  'psVersions.loadErrorTitle': "Couldn't load version history",
  'psVersions.loadErrorDesc': 'The set may have been removed, or you may not have access.',
  'psVersions.backToDashboard': 'Back to dashboard',
  'psVersions.title': 'Version history',
  'psVersions.description':
    'Every published content version for this problem set. Newer assignments pin newer versions; existing assignments stay on the version they were given.',
  'psVersions.backToPreview': '← Back to preview',
  'psVersions.emptyTitle': 'No versions yet',
  'psVersions.emptyDesc': 'A version is minted the first time this set is assigned or re-synced.',
  'psVersions.version': 'v{number}',
  'psVersions.target': 'Target',
  'psVersions.against': 'Against',
  'psVersions.questionsCountOne': '{count} question',
  'psVersions.questionsCountMany': '{count} questions',
  'psVersions.pinnedOne': '{count} pinned assignment',
  'psVersions.pinnedMany': '{count} pinned assignments',
  'psVersions.diffStart': 'Click a version to start a diff.',
  'psVersions.diffSecond': 'Click another version to compare against.',
  'psVersions.diff': 'Diff',
  'psVersions.clearSelection': 'Clear selection',
  'psVersions.diffLoadError': "Couldn't load the diff. Try again.",
  'psVersions.diffAddedOne': '{count} question added',
  'psVersions.diffAddedMany': '{count} questions added',
  'psVersions.diffRemovedOne': '{count} question removed',
  'psVersions.diffRemovedMany': '{count} questions removed',
  'psVersions.diffAnswerOne': '{count} answer changed',
  'psVersions.diffAnswerMany': '{count} answers changed',
  'psVersions.diffContentOne': '{count} cosmetic content edit',
  'psVersions.diffContentMany': '{count} cosmetic content edits',
  'psVersions.comparing': 'Comparing {target} against {against}.',
  'psVersions.initialVersion': 'nothing (initial version)',
  'psVersions.identical': 'Identical content.',

  // ── LA-6e-3: classroom join-code widget ──────────────────────────────
  'classCode.heading': 'Classroom Join Code',
  'classCode.noExpiry': 'No expiry',
  'classCode.expired': 'Expired',
  'classCode.expiresLtHour': 'Expires in <1 hour',
  'classCode.expiresHours': 'Expires in {hours} hours',
  'classCode.expiresDays': 'Expires in {days} days',
  'classCode.copied': 'Copied!',
  'classCode.copyCode': 'Copy code',
  'classCode.copyLink': 'Copy link',
  'classCode.regenConfirmAria': 'Confirm regenerate join code',
  'classCode.regenWarning':
    "Regenerating invalidates the current code. Students who haven't joined yet will need the new one.",
  'classCode.yesRegenerate': 'Yes, regenerate',
  'classCode.regenerate': 'Regenerate',
  'classCode.regenerateTitle': 'Regenerate join code for {classroom}',
  'classCode.generating': 'Generating…',
  'classCode.generate': 'Generate Join Code',

  // ── Streak badge ─────────────────────────────────────────────────────
  'streak.days': '{count}-day streak',
  'streak.tierStarter': 'On Fire',
  'streak.tierWeek': 'Week Warrior',
  'streak.tierMonth': 'Month Master',
  'streak.tierChampion': 'Champion',
  'streak.best': 'best: {count}',
  'streak.grace': 'grace ✓',
  'streak.graceTitle': 'Grace day used — streak preserved through one missed day',

  // ── Connectivity (PWA offline indicator) ─────────────────────────────
  'connectivity.offlineTitle': "You're offline",
  'connectivity.offlineBanner': "You're offline — showing saved work.",
  'connectivity.backOnline': 'Back online.',

  // ── Sync (offline write queue status — MSO-8) ────────────────────────
  'sync.savedOffline': "Saved on this device · will sync when you're back online",
  'sync.syncing': 'Syncing…',
  'sync.synced': 'Saved',
  'sync.syncFailed': "Couldn't sync — will retry",
  'sync.pendingOne': '{count} change waiting to sync',
  'sync.pendingMany': '{count} changes waiting to sync',

  // ── PWA (install to home screen) ─────────────────────────────────────
  'pwa.installPrompt': 'Add OpenShiksha to your home screen.',
  'pwa.install': 'Install',
  'pwa.installDismiss': 'Dismiss install prompt',
  // ── PWA (service worker update prompt) ───────────────────────────────
  'pwa.updateAvailable': 'A new version is available.',
  'pwa.refresh': 'Refresh',
  // ── Web Push notifications (due-date reminders) ──────────────────────
  'push.prompt': 'Get reminders on your phone.',
  'push.enable': 'Turn on',
  'push.dismiss': 'Dismiss notification prompt',
  'push.enabled': 'Reminders on',
  'push.label': 'Push notifications',
  'push.description': 'Get a notification on this device when an assignment is due soon.',
  'push.blocked': 'Notifications are blocked in your browser settings.',
} as const;

/** Every valid i18n key. Derived from the English dictionary. */
export type LocaleKey = keyof typeof en;

/** Shape every non-English locale must satisfy — full key parity, enforced by tsc. */
export type LocaleDict = Record<LocaleKey, string>;
